import { useState, useCallback } from 'react'

export interface WrappedPromiseResult<T, A> {
  data: T | undefined
  error: Error | undefined
  isLoading: boolean
  execute: (arg?: A) => void
}

export interface UseWrappedPromiseOptions<T> {
  onSuccess?: (data: T) => void
}

export function useWrappedPromise<T, A>(
  promise: (arg?: A) => Promise<T>,
  options?: UseWrappedPromiseOptions<T>
): WrappedPromiseResult<T, A> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const execute = useCallback(
    (arg?: A) => {
      setIsLoading(true)
      setData(undefined)
      setError(undefined)

      promise(arg)
        .then((response) => {
          setData(response)
          setIsLoading(false)
          if (options?.onSuccess) {
            options.onSuccess(response)
          }
        })
        .catch((err) => {
          setError(err)
          setIsLoading(false)
        })
    },
    [promise, options]
  )

  return { data, error, isLoading, execute }
}
